import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { Edit, Info, DollarSign, TrendingUp, TrendingDown, ChevronDown, ChevronUp } from "lucide-react";
import EditableMetricsDialog from "./EditableMetricsDialog";
import './SavingsBreakdown.css';

const MIN_SAVINGS_RATE = 7; // percent
const MAX_SAVINGS_RATE = 25; // percent

const formatCurrency = (value, fractionDigits = 2) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
}).format(value);

const formatPercent = (value, fractionDigits = 1) => `${value.toFixed(fractionDigits)}%`;

const SavingsBreakdown = ({ data, onSavingsUpdate }) => {
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [isCalculationOpen, setIsCalculationOpen] = useState(false);
    const [efficiencyParams, setEfficiencyParams] = useState(() => ({
        totalFTEs: data.efficiency_parameters?.total_ftes ?? 0,
        avgSalary: data.efficiency_parameters?.avg_fte_salary ?? 0,
        overallAutomation: data.efficiency_parameters?.overall_automation_potential ?? 0,
    }));
    const [effectivenessParams, setEffectivenessParams] = useState(() => ({
        totalSpend: data.effectiveness_parameters?.total_spend ?? 0,
        spendUnderManagement: data.effectiveness_parameters?.spend_under_management_percent ?? 0,
        minSavingsRate: MIN_SAVINGS_RATE,
        maxSavingsRate: MAX_SAVINGS_RATE,
    }));

    useEffect(() => {
        if (data.efficiency_parameters) {
            setEfficiencyParams({
                totalFTEs: data.efficiency_parameters.total_ftes,
                avgSalary: data.efficiency_parameters.avg_fte_salary,
                overallAutomation: data.efficiency_parameters.overall_automation_potential,
            });
        }
    }, [data.efficiency_parameters]);

    useEffect(() => {
        if (data.effectiveness_parameters) {
            setEffectivenessParams((prev) => ({
                totalSpend: data.effectiveness_parameters.total_spend,
                spendUnderManagement: data.effectiveness_parameters.spend_under_management_percent,
                minSavingsRate: MIN_SAVINGS_RATE,
                maxSavingsRate: MAX_SAVINGS_RATE,
                ...(prev.minSavingsRate !== MIN_SAVINGS_RATE || prev.maxSavingsRate !== MAX_SAVINGS_RATE
                    ? { minSavingsRate: prev.minSavingsRate, maxSavingsRate: prev.maxSavingsRate }
                    : {}),
            }));
        }
    }, [data.effectiveness_parameters]);

    const calculations = useMemo(() => {
        const automationDecimal = (efficiencyParams.overallAutomation ?? 0) / 100;
        const efficiency = (efficiencyParams.totalFTEs ?? 0) * (efficiencyParams.avgSalary ?? 0) * automationDecimal;
        const unmanagedFraction = Math.max(0, 1 - (effectivenessParams.spendUnderManagement ?? 0) / 100);
        const upliftRate = automationDecimal * 0.5;
        const totalSpend = effectivenessParams.totalSpend ?? 0;
        const effectivenessMin = totalSpend * unmanagedFraction * upliftRate * ((effectivenessParams.minSavingsRate ?? MIN_SAVINGS_RATE) / 100);
        const effectivenessMax = totalSpend * unmanagedFraction * upliftRate * ((effectivenessParams.maxSavingsRate ?? MAX_SAVINGS_RATE) / 100);
        const totalMin = Math.min(efficiency, effectivenessMin);
        const totalMax = Math.max(efficiency, effectivenessMax);
        return {
            efficiency,
            effectivenessMin,
            effectivenessMax,
            totalMin,
            totalMax,
            unmanagedFraction,
            upliftRate,
        };
    }, [efficiencyParams, effectivenessParams]);

    useEffect(() => {
        onSavingsUpdate?.({
            efficiency: calculations.efficiency,
            effectivenessMin: calculations.effectivenessMin,
            effectivenessMax: calculations.effectivenessMax,
            totalMin: calculations.totalMin,
            totalMax: calculations.totalMax,
        });
    }, [calculations, onSavingsUpdate]);

    const totalSpendInfo = formatCurrency(effectivenessParams.totalSpend ?? 0);
    const unmanagedPercent = (calculations.unmanagedFraction ?? 0) * 100;
    const savingsRateText = `${formatPercent(effectivenessParams.minSavingsRate ?? MIN_SAVINGS_RATE)} - ${formatPercent(effectivenessParams.maxSavingsRate ?? MAX_SAVINGS_RATE)}`;

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="sb-header">
                        <div className="sb-header-title">
                            <DollarSign className="sb-icon-success"/>
                            <CardTitle>Total Savings Range</CardTitle>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
                            <Edit className="sb-icon-btn"/>
                            Edit Inputs
                        </Button>
                    </div>
                    <CardDescription>
                        Projected annual savings from AI implementation across all processes
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="sb-content">
                        {/* Main savings display */}
                        <div className="sb-savings-main">
                            <div className="sb-savings-main-inner">
                                <p className="sb-savings-label">Total Annual Savings</p>
                                <p className="sb-savings-value">
                                    {formatCurrency(calculations.totalMin)} - {formatCurrency(calculations.totalMax)}
                                </p>
                            </div>
                        </div>

                        {/* Two cards side by side */}
                        <div className="sb-cards-grid">
                            {/* Efficiency card */}
                            <div className="sb-card sb-card-efficiency">
                                <div className="sb-card-header">
                                    <div className="sb-card-title-wrapper">
                                        <TrendingUp className="sb-icon-primary"/>
                                        <h4 className="sb-card-title">Efficiency Savings</h4>
                                    </div>
                                    <Badge variant="secondary" className="sb-badge-efficiency">
                                        Bandwidth Unlocked
                                    </Badge>
                                </div>
                                <p className="sb-card-value sb-value-primary">{formatCurrency(calculations.efficiency)}</p>
                                <div className="sb-card-details">
                                    <div className="sb-detail-row">
                                        <span className="sb-detail-label">Formula:</span>
                                        <span className="sb-detail-value">Σ(No of FTEs × Avg FTE Salary × Automation%)</span>
                                    </div>
                                    <p className="sb-detail-subtext">
                                        {efficiencyParams.totalFTEs.toLocaleString()} FTEs × $
                                        {(efficiencyParams.avgSalary / 1000).toFixed(1)}K × {formatPercent(efficiencyParams.overallAutomation, 1)}
                                    </p>
                                </div>
                            </div>

                            {/* Effectiveness card */}
                            <div className="sb-card sb-card-effectiveness">
                                <div className="sb-card-header">
                                    <div className="sb-card-title-wrapper">
                                        <TrendingDown className="sb-icon-accent"/>
                                        <h4 className="sb-card-title">Effectiveness Savings</h4>
                                    </div>
                                    <Badge variant="secondary" className="sb-badge-effectiveness">
                                        Value Creation
                                    </Badge>
                                </div>
                                <p className="sb-card-value sb-value-accent">
                                    {formatCurrency(calculations.effectivenessMin)} - {formatCurrency(calculations.effectivenessMax)}
                                </p>
                                <div className="sb-effectiveness-details">
                                    <p>Formula: Total Spend × Unmanaged% × Uplift Rate × Savings Rate</p>
                                    <p>Total Spend: {totalSpendInfo}</p>
                                    <p>Unmanaged Portion: {formatPercent(unmanagedPercent)}</p>
                                    <p>
                                        Uplift Rate: {formatPercent(calculations.upliftRate * 100, 1)} (Overall Automation × 50%)
                                    </p>
                                    <p>Savings Rate: {savingsRateText}</p>
                                </div>
                            </div>
                        </div>

                        {/* Three info boxes */}
                        <div className="sb-info-grid">
                            <div className="sb-info-box">
                                <p className="sb-info-label">Total Spend</p>
                                <p className="sb-info-value">{totalSpendInfo}</p>
                                <p className="sb-info-description">Input captured directly from dashboard data</p>
                            </div>
                            <div className="sb-info-box">
                                <p className="sb-info-label">Spend Under Management</p>
                                <p className="sb-info-value">
                                    {formatPercent(effectivenessParams.spendUnderManagement ?? 0)}
                                </p>
                                <p className="sb-info-description">
                                    {formatPercent(unmanagedPercent)} unmanaged opportunity
                                </p>
                            </div>
                            <div className="sb-info-box">
                                <p className="sb-info-label">Automation-Uplift Factor</p>
                                <p className="sb-info-value">
                                    {formatPercent(calculations.upliftRate * 100, 1)}
                                </p>
                                <p className="sb-info-description">
                                    Based on {formatPercent(efficiencyParams.overallAutomation, 1)} automation potential
                                </p>
                            </div>
                        </div>

                        {/* Collapsible section */}
                        <Collapsible open={isCalculationOpen} onOpenChange={setIsCalculationOpen}>
                            <CollapsibleTrigger asChild>
                                <Button variant="ghost" className="sb-collapsible-trigger">
                                    <div className="sb-collapsible-title">
                                        <Info className="sb-icon-info"/>
                                        <span className="sb-collapsible-text">Detailed Calculation Methodology</span>
                                    </div>
                                    {isCalculationOpen ? (
                                        <ChevronUp className="sb-icon-chevron"/>
                                    ) : (
                                        <ChevronDown className="sb-icon-chevron"/>
                                    )}
                                </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="sb-collapsible-content">
                                <Card className="sb-methodology-card">
                                    <CardContent className="sb-methodology-content">
                                        <div className="sb-methodology-section">
                                            <h4 className="sb-methodology-title">Step 1: Efficiency Savings</h4>
                                            <p className="sb-methodology-formula">Σ(FTE × Salary × Automation%)</p>
                                            <div className="sb-methodology-calculation">
                                                = {efficiencyParams.totalFTEs.toLocaleString()} × $
                                                {(efficiencyParams.avgSalary / 1000).toFixed(1)}K × {formatPercent(efficiencyParams.overallAutomation, 1)} ={" "}
                                                {formatCurrency(calculations.efficiency)}
                                            </div>
                                        </div>
                                        <div className="sb-methodology-section">
                                            <h4 className="sb-methodology-title">Step 2: Effectiveness Savings</h4>
                                            <p className="sb-methodology-formula">Total Spend × Unmanaged% × Uplift Rate × Savings Rate</p>
                                            <div className="sb-methodology-calculation">
                                                <div>Total Spend = {totalSpendInfo}</div>
                                                <div>Unmanaged% = {formatPercent(unmanagedPercent)}</div>
                                                <div>Uplift Rate = {formatPercent(calculations.upliftRate * 100, 1)}</div>
                                                <div>Min Savings Rate = {formatPercent(effectivenessParams.minSavingsRate ?? MIN_SAVINGS_RATE)}</div>
                                                <div>Max Savings Rate = {formatPercent(effectivenessParams.maxSavingsRate ?? MAX_SAVINGS_RATE)}</div>
                                                <div className="sb-methodology-divider">
                                                    Min Effectiveness = {formatCurrency(calculations.effectivenessMin)}
                                                </div>
                                                <div>Max Effectiveness = {formatCurrency(calculations.effectivenessMax)}</div>
                                            </div>
                                        </div>
                                        <div className="sb-methodology-section">
                                            <h4 className="sb-methodology-title">Step 3: Total Savings Range</h4>
                                            <div className="sb-methodology-calculation">
                                                <div>
                                                    Minimum = min({formatCurrency(calculations.efficiency)}, {formatCurrency(calculations.effectivenessMin)}) ={" "}
                                                    {formatCurrency(calculations.totalMin)}
                                                </div>
                                                <div>
                                                    Maximum = max({formatCurrency(calculations.efficiency)}, {formatCurrency(calculations.effectivenessMax)}) ={" "}
                                                    {formatCurrency(calculations.totalMax)}
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                </CardContent>
            </Card>
            <EditableMetricsDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                efficiency={efficiencyParams}
                effectiveness={effectivenessParams}
                onSave={(eff, effx) => {
                    setEfficiencyParams(eff);
                    setEffectivenessParams(effx);
                }}
            />
        </>
    );
};

export default SavingsBreakdown;
